import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, History, Check, MapPin, Package, Box } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { HistoryTimeline } from "@/components/HistoryTimeline";
import { ShipmentActions } from "@/components/forms/ShipmentActions";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const FLOW = ["Created", "Packed", "Shipped", "Delivered", "Waiting for Box Return", "Box Return In Transit", "Box Returned", "Closed"];
const FLOW_LABELS = {
  Created: "Created", Packed: "Packed", Shipped: "Shipped", Delivered: "Delivered",
  "Waiting for Box Return": "Installed", "Box Return In Transit": "Crate Returning",
  "Box Returned": "Crate Back", Closed: "Closed",
};

function Stepper({ status }) {
  const idx = FLOW.indexOf(status);
  if (idx === -1) return null;
  return (
    <div className="flex flex-wrap items-center gap-y-3">
      {FLOW.map((step, i) => (
        <div key={step} className="flex items-center">
          <div className="flex flex-col items-center">
            <span className={cn("flex h-7 w-7 items-center justify-center rounded-full border-2 text-xs font-semibold", i < idx ? "border-green-600 bg-green-600 text-white" : i === idx ? "border-primary bg-primary text-white" : "border-slate-300 bg-white text-slate-400")}>
              {i < idx ? <Check className="h-4 w-4" /> : i + 1}
            </span>
            <span className={cn("mt-1 w-20 text-center text-[11px] leading-tight", i <= idx ? "text-foreground" : "text-muted-foreground")}>{FLOW_LABELS[step]}</span>
          </div>
          {i < FLOW.length - 1 && (
            <div className={cn("mx-1 mb-5 h-0.5 w-6 sm:w-10", i < idx ? "bg-green-600" : "bg-slate-200")} />
          )}
        </div>
      ))}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm">{value ?? "—"}</p>
    </div>
  );
}

export default function ShipmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.role !== "client_user";
  const [s, setS] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, h] = await Promise.all([
        api.get(`/shipments/${id}`),
        api.get(`/shipments/${id}/history`),
      ]);
      setS(d.data); setEvents(h.data);
    } catch (err) {
      if (err?.response?.status === 404) navigate("/shipments");
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading || !s) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6">
      <Link to="/shipments" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to Shipments
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold tracking-tight">{s.shipment_code}</h2>
            <StatusBadge status={s.status} />
          </div>
          <p className="text-muted-foreground">{s.client_name} → {s.store_name}, {s.city}, {s.state}</p>
        </div>
      </div>
      <Card>
        <CardContent className="pt-6">
          <Stepper status={s.status} />
          {s.status === "Issue / Exception" && <p className="mt-2 rounded-md bg-red-50 p-3 text-sm text-red-800">This shipment has an open issue — see the action panel and timeline below.</p>}
        </CardContent>
      </Card>
      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><Package className="h-4 w-4" /> Product</CardTitle></CardHeader>
          <CardContent>
            {s.product ? (
              <>
                <Link to={`/products/${s.product_id}`} className="font-semibold text-primary hover:underline">{s.product.serial_number}</Link>
                <p className="text-sm text-muted-foreground">{s.product.product_type}</p>
                <div className="mt-1"><StatusBadge status={s.product.status} /></div>
              </>
            ) : <p className="text-sm text-muted-foreground">Not assigned</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><Box className="h-4 w-4" /> Wooden Crate</CardTitle></CardHeader>
          <CardContent>
            {s.crate ? (
              <>
                <Link to={`/crates/${s.crate_id}`} className="font-semibold text-primary hover:underline">{s.crate.crate_code}</Link>
                <div className="mt-1"><StatusBadge status={s.crate.status} /></div>
              </>
            ) : <p className="text-sm text-muted-foreground">Assigned at the Pack step</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><MapPin className="h-4 w-4" /> Destination</CardTitle></CardHeader>
          <CardContent>
            <p className="font-semibold">{s.store_name}</p>
            <p className="text-sm text-muted-foreground">{s.store_address}<br />{s.city}, {s.state} {s.zip}</p>
          </CardContent>
        </Card>
      </div>
      {isStaff && <ShipmentActions shipment={s} onAction={load} />}
      <Card>
        <CardHeader><CardTitle className="text-base">Shipment Details</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="From" value={s.from_location} />
          <Field label="Carrier" value={s.carrier} />
          <Field label="Outbound Tracking" value={s.outbound_tracking} />
          <Field label="Return Tracking" value={s.return_tracking} />
          <Field label="Shipped" value={s.shipped_date} />
          <Field label="Expected Delivery" value={s.expected_delivery_date} />
          <Field
            label="Delivered"
            value={
              s.delivered_date
                ? (s.delivery_delay_days != null
                    ? `${s.delivered_date} (${s.delivery_delay_days > 0 ? `${s.delivery_delay_days}d late` : s.delivery_delay_days < 0 ? `${-s.delivery_delay_days}d early` : 'on time'})`
                    : s.delivered_date)
                : null
            }
          />
          <Field label="Installed" value={s.installed_date} />
          <Field label="Crate Returned" value={s.box_returned_date} />
          {s.notes && (<div className="sm:col-span-2 lg:col-span-4"><Field label="Notes" value={s.notes} /></div>)}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><History className="h-4 w-4" /> Shipment Timeline</CardTitle></CardHeader>
        <CardContent><HistoryTimeline events={events} /></CardContent>
      </Card>
    </div>
  );
}
